import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ClipboardList,
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Calendar,
  XCircle,
  Loader2,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  User,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  draft: { label: 'Draft', class: 'bg-slate-100 text-slate-700', icon: FileText },
  submitted: { label: 'Submitted', class: 'bg-blue-100 text-blue-700', icon: Clock },
  scheduled: { label: 'Scheduled', class: 'bg-purple-100 text-purple-700', icon: Calendar },
  sample_collected: { label: 'Sample Collected', class: 'bg-cyan-100 text-cyan-700', icon: CheckCircle },
  in_progress: { label: 'In Progress', class: 'bg-amber-100 text-amber-700', icon: Loader2 },
  completed: { label: 'Completed', class: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-700', icon: XCircle },
};

const priorityConfig = {
  routine: { label: 'Routine', class: 'bg-slate-100 text-slate-700' },
  urgent: { label: 'Urgent', class: 'bg-orange-100 text-orange-700' },
  stat: { label: 'STAT', class: 'bg-red-100 text-red-700' },
};

export const ReportOrdersPage = () => {
  const { api, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [patients, setPatients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
  });

  const [newOrder, setNewOrder] = useState({
    order_type: '',
    priority: 'routine',
    patient_id: '',
    dept_id: '',
    test_names: '',
    clinical_notes: '',
  });

  const [scheduleData, setScheduleData] = useState({
    scheduled_date: '',
    scheduled_location: '',
  });

  useEffect(() => {
    fetchOrders();
    fetchPatients();
    fetchDepartments();
  }, [page, filters]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);

      const response = await api.get(`/orders?${params.toString()}`);
      setOrders(response.data.orders || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      // Handle paginated response format
      setPatients(response.data?.patients || response.data || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      // Handle paginated response format
      setDepartments(response.data?.departments || response.data || []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      const orderData = {
        ...newOrder,
        org_id: user.org_id,
        test_names: newOrder.test_names.split(',').map(t => t.trim()).filter(Boolean),
        test_codes: [],
        ordering_physician_id: user.role === 'clinician' ? user.user_id : null,
      };

      await api.post('/orders', orderData);
      toast.success('Order created successfully');
      setIsCreateOpen(false);
      setNewOrder({
        order_type: '',
        priority: 'routine',
        patient_id: '',
        dept_id: '',
        test_names: '',
        clinical_notes: '',
      });
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  const handleScheduleOrder = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/orders/${selectedOrder.order_id}/schedule`, null, {
        params: scheduleData
      });
      toast.success('Order scheduled successfully');
      setIsScheduleOpen(false);
      setSelectedOrder(null);
      setScheduleData({ scheduled_date: '', scheduled_location: '' });
      fetchOrders();
    } catch (error) {
      toast.error('Failed to schedule order');
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/cancel`);
      toast.success('Order cancelled');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
    if (value && value !== 'all') {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="report-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Report Orders</h1>
          <p className="text-muted-foreground mt-1">
            Request and track diagnostic tests
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-order-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Test Order</DialogTitle>
              <DialogDescription>
                Request a new diagnostic test for a patient
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateOrder} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Test Type *</Label>
                  <Select
                    value={newOrder.order_type}
                    onValueChange={(v) => setNewOrder(prev => ({ ...prev, order_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="laboratory">Laboratory</SelectItem>
                      <SelectItem value="pathology">Pathology</SelectItem>
                      <SelectItem value="radiology">Radiology</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={newOrder.priority}
                    onValueChange={(v) => setNewOrder(prev => ({ ...prev, priority: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="stat">STAT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Patient *</Label>
                <Select
                  value={newOrder.patient_id}
                  onValueChange={(v) => setNewOrder(prev => ({ ...prev, patient_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => (
                      <SelectItem key={p.patient_id} value={p.patient_id}>
                        {p.full_name} {p.mrn_member_id ? `(${p.mrn_member_id})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Department *</Label>
                <Select
                  value={newOrder.dept_id}
                  onValueChange={(v) => setNewOrder(prev => ({ ...prev, dept_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => (
                      <SelectItem key={d.dept_id} value={d.dept_id}>
                        {d.dept_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tests Requested</Label>
                <Input
                  value={newOrder.test_names}
                  onChange={(e) => setNewOrder(prev => ({ ...prev, test_names: e.target.value }))}
                  placeholder="CBC, BMP, Lipid Panel (comma separated)"
                />
              </div>

              <div className="space-y-2">
                <Label>Clinical Notes</Label>
                <Textarea
                  value={newOrder.clinical_notes}
                  onChange={(e) => setNewOrder(prev => ({ ...prev, clinical_notes: e.target.value }))}
                  placeholder="Enter clinical indication or notes..."
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Order</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { status: 'submitted', label: 'Pending', icon: Clock, color: 'blue' },
          { status: 'scheduled', label: 'Scheduled', icon: Calendar, color: 'purple' },
          { status: 'in_progress', label: 'In Progress', icon: Loader2, color: 'amber' },
          { status: 'completed', label: 'Completed', icon: CheckCircle, color: 'green' },
        ].map(({ status, label, icon: Icon, color }) => {
          const count = orders.filter(o => o.status === status).length;
          return (
            <Card
              key={status}
              className={`cursor-pointer transition-all hover:-translate-y-0.5 ${
                filters.status === status ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => handleFilterChange('status', filters.status === status ? '' : status)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 text-${color}-600`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-muted-foreground">{label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search orders..." className="pl-10" />
            </div>
            <Select
              value={filters.status || "all"}
              onValueChange={(v) => handleFilterChange('status', v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.priority || "all"}
              onValueChange={(v) => handleFilterChange('priority', v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="routine">Routine</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="stat">STAT</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : orders.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Order ID</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => {
                    const StatusIcon = statusConfig[order.status]?.icon || FileText;
                    return (
                      <TableRow key={order.order_id} data-testid={`order-row-${order.order_id}`}>
                        <TableCell className="font-mono text-sm">
                          {order.order_id.slice(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{order.patient_name || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground">
                                {order.patient_mrn ? `MRN: ${order.patient_mrn}` : '-'}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{order.order_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={priorityConfig[order.priority]?.class}>
                            {priorityConfig[order.priority]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig[order.status]?.class}>
                            {statusConfig[order.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {order.scheduled_date 
                            ? new Date(order.scheduled_date).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/orders/${order.order_id}`}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Details
                                </Link>
                              </DropdownMenuItem>
                              {order.status === 'submitted' && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedOrder(order);
                                    setIsScheduleOpen(true);
                                  }}
                                >
                                  <Calendar className="w-4 h-4 mr-2" />
                                  Schedule
                                </DropdownMenuItem>
                              )}
                              {order.linked_report_id && (
                                <DropdownMenuItem asChild>
                                  <Link to={`/reports/${order.linked_report_id}`}>
                                    <FileText className="w-4 h-4 mr-2" />
                                    View Report
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              {!['completed', 'cancelled'].includes(order.status) && (
                                <DropdownMenuItem
                                  onClick={() => handleCancelOrder(order.order_id)}
                                  className="text-red-600"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <ClipboardList className="w-12 h-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No orders found</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Dialog */}
      <Dialog open={isScheduleOpen} onOpenChange={setIsScheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Test</DialogTitle>
            <DialogDescription>
              Set appointment date and location for this test
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleScheduleOrder} className="space-y-4">
            <div className="space-y-2">
              <Label>Date & Time *</Label>
              <Input
                type="datetime-local"
                value={scheduleData.scheduled_date}
                onChange={(e) => setScheduleData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                value={scheduleData.scheduled_location}
                onChange={(e) => setScheduleData(prev => ({ ...prev, scheduled_location: e.target.value }))}
                placeholder="Lab name or location"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsScheduleOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Schedule</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
